import React, { useState, useMemo } from 'react'
import classNames from 'classnames'
import { format } from 'currency-formatter'
import { useDispatch } from 'react-redux'
import { Modal, Row, Col } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'

import { EnrolState } from 'enum'
import { IRequestCourseModel } from 'models/requestCourse.model'
import UserAvatar from 'components/ui/user-avatar/UserAvatar'
import { dateParse } from 'helpers/date.helper'
import Button from 'design-system/button'
import { ButtonSizes, ButtonVariant } from 'design-system/button/src/Button'
import { Actions } from 'store/actions/requestOutsideCourses.action'
import { useIsDesktop, cleanUrl } from 'helpers'
import { useAccess } from 'contexts/AccessContext'
import { joinKeys } from '../../../../helpers/translations.helper'
import { prefix } from './constants'
import { Buttons, RequestAdmin } from './components'
import styles from './ModalDetails.module.scss'

interface ICourseProps {
  requestCourse: IRequestCourseModel
  isAdmin: boolean
}

const ModalDetails: React.FC<ICourseProps> = ({ requestCourse, isAdmin }) => {
  const { t } = useTranslation()
  const { name, senceCode, user, message, hour, cost, urlCourse } =
    requestCourse
  const dispatch = useDispatch()

  const [show, setShow] = useState(false)
  const [noteAdminField, setNoteAdminField] = useState('')
  const handleClose = () => setShow(false)
  const handleShow = () => setShow(true)
  const userArea = useMemo(() => user?.area?.name, [user])

  const handleSaveAdmin = React.useCallback(
    (state: EnrolState) => {
      dispatch(
        Actions.edit({
          params: {
            id: requestCourse.id,
            stateAdmin: state,
            answerAdmin: noteAdminField,
          },
          onSuccess: () => {
            handleClose()
          },
        })
      )
    },
    [requestCourse.id, dispatch, noteAdminField]
  )

  const requestAdmin = requestCourse.stateAdmin === EnrolState.Pending
  const isDesktop = useIsDesktop()
  const { can } = useAccess()
  const canManageRequests = isAdmin || can('manageCourseRequests')

  const messages = useMemo(() => {
    const truncateMessage = (message?: string, expanded = false) => {
      if (!message) return `[${t(`${prefix}.noMessage`)}].`
      let result = message
      const max = 188
      if (result.length > max && !expanded) {
        const substring = (end) => message.substring(0, end)
        const aux = substring(max).lastIndexOf(' ')
        result = aux > 0 ? substring(aux) : substring(max - 4) + '...'
      }
      return result + ' '
    }
    return {
      answerAdmin: truncateMessage(requestCourse.answerAdmin),
    }
  }, [requestCourse, t])

  const buttons =
    requestAdmin && canManageRequests ? (
      <Buttons note={noteAdminField} onSelect={handleSaveAdmin} />
    ) : (
      <Button
        size={ButtonSizes.Small}
        variant={ButtonVariant.Secondary}
        onClick={handleClose}
      >
        {t(`${prefix}.close`)}
      </Button>
    )

  return (
    <>
      <Button
        className={`${styles.openModalButton} float-right`}
        size={ButtonSizes.ExtraSmall}
        onClick={handleShow}
      >
        {t('home.myTeam.details')}
      </Button>
      <Modal
        show={show}
        size="lg"
        className={styles.modalContainer}
        onHide={handleClose}
        centered
      >
        <Modal.Header translate="yes" closeButton className="p-0 pb-2">
          <Modal.Title className={styles.modalTitle}>
            {t(`${prefix}.title`)}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className={`p-0 pt-sm-4 ${styles.modalBody}`}>
          <Row>
            <Col xs={12} sm={5}>
              <UserAvatar user={user} className={`${styles.avatar}`}>
                {userArea && (
                  <small
                    className={`grey-darken d-none d-sm-block ${styles.userArea}`}
                  >
                    {userArea}
                  </small>
                )}
              </UserAvatar>
              <p className={`${styles.title} mb-0 mt-3 mt-sm-4`}>
                {t(`${prefix}.course`)}
              </p>
              <p className={`${styles.label} ${styles.courseName} mb-2`}>
                {name}
              </p>
            </Col>
            <Col xs={12} sm={7} className="pr-sm-5">
              {isDesktop && (
                <>
                  <p className={`${styles.title}`}>{t(`${prefix}.message`)}</p>
                  <p className={`${styles.label} mt-1 mb-1`}>{message}</p>
                </>
              )}
            </Col>
            <Col>
              <p className={`${styles.title}`}>{t('home.myTeam.table.type')}</p>
              <p className={classNames(`d-block mt-1 mb-1`)}>
                {t(joinKeys('administration.applications.typeRequests', 'DNC'))}
              </p>
            </Col>
          </Row>
          <Row>
            <Col xs={12} sm={5}>
              <p className={`${styles.title} mb-0 mt-1`}>
                {t(`${prefix}.date`)}
              </p>
              <p className={`${styles.label} mb-0 mt-1`}>
                {dateParse(requestCourse.startDate)} /{' '}
                {dateParse(requestCourse.endDate)}
              </p>
              {senceCode && (
                <>
                  <p className={`${styles.title} mb-0 mt-1`}>
                    {t(`${prefix}.codeSence`)}
                  </p>
                  <p className={`${styles.label} mb-0 mt-1`}>{senceCode}</p>
                </>
              )}
            </Col>
            <Col xs={12} sm={7} className="pr-sm-5">
              <p className={`${styles.title} mb-0 mt-1`}>
                {t(`${prefix}.hours`)}
              </p>
              <p className={`${styles.label} mt-1 mb-1`}>{hour}</p>
              <p className={`${styles.title} mb-0 mt-1`}>
                {t(`${prefix}.cost`)}
              </p>
              <p className={`${styles.label} mt-1 mb-1`}>
                {format(+cost, {
                  thousand: ',',
                  decimal: '.',
                  precision: 0,
                })}
              </p>
            </Col>
            <Col xs={12}>
              <p className={`${styles.title}`}>{t(`${prefix}.link`)}</p>
              <a
                href={`//${cleanUrl(urlCourse)}`}
                target="_blank"
                className={classNames(`d-block mt-1 mb-1`, styles.link)}
                rel="noreferrer"
              >
                {urlCourse}
              </a>
            </Col>
            <Col xs={12}>
              <RequestAdmin
                requestCourse={requestCourse}
                text={messages.answerAdmin}
                value={noteAdminField}
                onChange={(value) => setNoteAdminField(value)}
                canRespond={canManageRequests}
              />
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer className="pt-2 pb-0 px-0 mt-4">{buttons}</Modal.Footer>
      </Modal>
    </>
  )
}

export default ModalDetails
